// @ts-strict-ignore
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EnvironmentEnum } from 'src/app/models/environment-enum';
import { LoginService } from 'src/app/services/login.service';
import { environment } from '../../../environments/environment';
import { AnalyticsService } from '../../services/analytics.service';
import { SessionService } from '../../services/session.service';

@Component({
  selector: 'login',
  templateUrl: './login.component.html',
  styleUrl: 'login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
  public isModernBrowser: boolean = false;
  public verificationCode: string;
  public routeProvided: string;
  public isLoading: boolean = false;
  private pageClosed = new Subject<void>();
  private supportedBrowsers: string[] = ['chrome', 'edge', 'firefox'];
  public isMobileView: boolean = false;
  private autoLoginToken: string;
  private autoLoginRefreshToken: string;
  private readonly activatedRoute = inject(ActivatedRoute);

  constructor(
    private readonly sessionService: SessionService,
    private readonly loginService: LoginService,
    private readonly analyticsService: AnalyticsService,
  ) {}

  public ngOnInit(): void {
    // Feature detection to distinguish a modern browser from an old one
    const isSupportedBrowser = this.isSupportedBrowser();

    this.activatedRoute.queryParams
      .pipe(takeUntil(this.pageClosed))
      .subscribe((params) => {
        if (params['token']) {
          this.autoLoginToken = params['token'];
        }
        if (params['refreshToken']) {
          this.autoLoginRefreshToken = params['refreshToken'];
        }
      });

    if (this.autoLoginRefreshToken && this.autoLoginToken) {
      this.sessionService.removeUserSession();
      sessionStorage.clear();
      this.sessionService.setUserSession({
        token: this.autoLoginToken,
        refreshToken: this.autoLoginRefreshToken,
      });
      this.analyticsService.sendEventAnalytics('login', 'successful');
      this.loginService.setDefaultMember();
      this.loginService.setEntityType();
      this.loginService.goToProperRoute(this.routeProvided);
    }

    if (typeof EventSource !== 'undefined' && isSupportedBrowser) {
      this.isModernBrowser = true;
    }

    this.isMobileView =
      this.isDeviceRequiredSize() && environment.name !== EnvironmentEnum.Local;

    this.handleQueryParams();

    if (this.sessionService.getToken()) {
      this.loginService.goToProperRoute(this.routeProvided);
    }
    this.analyticsService.sendPageViewAnalytics('/login-form', 'Login');
  }

  public ngOnDestroy(): void {
    this.pageClosed.next();
    this.pageClosed.complete();
  }

  private handleQueryParams(): void {
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.pageClosed))
      .subscribe((params) => {
        if (params['page']) {
          this.routeProvided = params['page'];
        }
      });
  }

  private isSupportedBrowser(): boolean {
    const agent = window.navigator.userAgent.toLowerCase();
    let browsername = '';
    switch (true) {
      case agent.indexOf('edge') > -1:
        browsername = 'edge';
        break;
      case agent.indexOf('opr') > -1 && !!(window as any).opr:
        browsername = 'opera';
        break;
      case agent.indexOf('chrome') > -1 && !!(window as any).chrome:
        browsername = 'chrome';
        break;
      case agent.indexOf('trident') > -1:
        browsername = 'ie';
        break;
      case agent.indexOf('firefox') > -1:
        browsername = 'firefox';
        break;
      case agent.indexOf('safari') > -1:
        browsername = 'safari';
        break;
      default:
        browsername = 'other';
    }
    return environment.name !== EnvironmentEnum.Staging &&
      environment.name !== EnvironmentEnum.Prod
      ? true
      : this.supportedBrowsers.includes(browsername);
  }

  private isDeviceRequiredSize(): boolean {
    return window.matchMedia('only screen and (max-width: 1025px)').matches;
  }
}
